/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 스케쥴 캐러셀 컴포넌트
 * 버전 : 1.0
 * 파일명 : CarouselSchedule.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-07-02
 * 수정 : 2025-07-04, 강동형 : 비어있을때 조건 추가
 * ================================================================ 
 * 수정 : 2025-07-07, 강동형 : 클릭해서 이동하는 로직 추가
 * ================================================================ */

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { ArmyRecruitDataType, LoadArmyRecruit } from "../recruitment/LoadArmyRecruit";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon_EmptyCalendar } from "../common/CommonIcons";
import { useRecruitStore } from "src/stores/RecruitStore";
import { useNavigate } from "react-router-dom";
import { CalendarEvent } from "../adminRecruit/AdRecruitCalendar";

const ScheduleItem = styled.div`
    padding: 1rem;
    background-color: #f9f9f9;
    border-radius: 5px;

    white-space: nowrap;
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.20);
    min-width: 100%;
    /* flex-shrink: 0; */
`;

const EmptyScheduleItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 5px;
  white-space: nowrap;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.20);
  min-width: 100%;
  height:156px;
  color: #444;
  font-size: 14px;
  font-weight: 600;
`

const ScheduleText = styled.p`
  margin: 0.2rem;
  line-height: 1;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
`;

const SmallBoxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem; // 박스 사이의 간격을 설정
`;

const ScheduleKindBox = styled.div`
  // background-color: #F5F5F5; // 회색 배경
  background: #F4F4F4;
  // color: #1976FF; // 파란색 글씨
  color: #035324;
  display: inline-block;
  padding: 0.1rem 0;
  border-radius: 5px;
  font-size: 0.8rem; // 작은 글씨;
  min-width: 46px;
  text-align: center;
`;

const ScheduleDdayBox = styled.div`
  // background-color: #1976FF; // 파란색 배경
  background: rgba(5, 136, 87, 0.90);
  color: white; // 흰색 글씨
  display: inline-block;
  padding: 0.1rem 0.3rem;
  border-radius: 5px;
  font-size: 0.8rem; // 작은 글씨;
  min-width: 42px;
  text-align: center;
`;

const ScheduleDescription = styled.p`
  font-weight: 500;
  margin: 0.2rem 0;
  margin-bottom : 1rem;;

    /* 텍스트 넘침 처리 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScheduleDeadline = styled.p`
  font-size: 12px; // 작은 글씨
  font-weight: 500;
  margin: 0.5rem 0;;
  color: gray;
`;

type schedulesEvent = {
  kind: string,
  dday: string,
  description: string,
  deadline: string,
  announce_id : number,
}

interface Props {
  setCurrentId: React.Dispatch<React.SetStateAction<number>>;
}

const CarouselSchedule = ({setCurrentId}:Props) => {
    const [data,setData] = useState<ArmyRecruitDataType[]>([])
    const [currentSlide, setCurrentSlide] = useState(0);
    const setField = useRecruitStore((state)=>state.setField)
    const navigate = useNavigate()

    const [sliderRef, instanceRef] = useKeenSlider({
        loop: true,
        slides: {
            perView: 'auto',
            spacing:10
        },
        slideChanged(slider) {
          setCurrentSlide(slider.track.details.rel); // 현재 인덱스 상태 업데이트
        },
    });

    const today = new Date()
    today.setHours(0, 0, 0, 0);
    const convertToSchdule = (data:ArmyRecruitDataType[]): schedulesEvent[]=>{
        return data.map(item => {
        const endDate = new Date(item.application_edate); // 문자열을 Date로 변환
        const diffTime = endDate.getTime() - today.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // ms → 일수 변환

        if (diffDays < 0 || diffDays > 60) return null;

        return {
            kind: item.recruitment_type,
            dday: diffDays === 0 ? 'D-day' : `D-${Math.abs(diffDays)}`,
            description: item.title,
            deadline: `서류접수마감일 ${item.application_edate.replaceAll('-', '.')}`,
            announce_id : item.announce_id
        };
        })
        .filter((event): event is schedulesEvent => event !== null); // ✅ 타입 보장
    }
    const schedules = convertToSchdule(data)

    useEffect(() => {
        const today = new Date();
        let nextMonth = today.getMonth() + 2; // getMonth()는 0부터 시작하므로 +1로 현재월, +2로 다음달
        let nextYear = today.getFullYear();

        if (nextMonth > 12) {
          nextMonth = 1;
          nextYear += 1;
        }
        const fetchData = async () => {
        const response = await LoadArmyRecruit({year:nextYear,month:nextMonth,limit_cnt:10});      
        setData(response.data)
        }
        fetchData()
    }, []);

    useEffect(()=>{
      const current = schedules[currentSlide];
      if (current) {
        setCurrentId(current.announce_id);
      }
    },[currentSlide, schedules])


    useEffect(() => {
        if (instanceRef.current) {
            instanceRef.current.update(); // 슬라이드 수가 변했을 때 다시 계산
        }
    }, [schedules.length]); // ← 슬라이드 데이터가 들어온 이후에만 실행

    const onClickToRecruit = (schedule_id:number)=>{
      const targetItem = data.find((item) => item.announce_id === schedule_id);
  
      if (!targetItem) return; // targetItem이 없으면 early return
  
      const convertToEvents = (data: ArmyRecruitDataType[]): CalendarEvent[] => {
        return data.map(item => {
          // 먼저 item.etc를 사용해 기존의 이벤트를 변환
          const events = item.schedules.map(schedule => {
            return {
              title: item.title,
              start: new Date(schedule.start_date),
              end: new Date(schedule.end_date),
              announce_id: item.announce_id,
              course_type: schedule.course_name === '입영일' || schedule.course_name === '임관일' || schedule.course_name === '접수일정' ? schedule.course_name : '추가과정',
              course_name: schedule.course_name,
              course_status:item.status,
              quilify : item.requirements,
              urlLink : item.source_url,
              sessionData: item.schedules,
              recruitment_type : item.recruitment_type
            };
          });
          return [...events];
        }).flat();
      };
      const convertedEvents = convertToEvents([targetItem])[0];
  
      setField('selected',convertedEvents)
      setField('isOpen',true)
      navigate('/recruitment')
    }

    return (
      <>
        {schedules.length !== 0 ? 
        (<>
          <div ref={sliderRef} className="keen-slider" style={{padding:'10px'}}>
              {schedules.map((schedule, index) => (
                  <ScheduleItem key={index} onClick={()=>onClickToRecruit(schedule.announce_id)} className="keen-slider__slide">
                      <SmallBoxContainer>
                      <ScheduleKindBox>
                          <ScheduleText>{schedule.kind}</ScheduleText>
                      </ScheduleKindBox>
                      <ScheduleDdayBox>
                          <ScheduleText>{schedule.dday}</ScheduleText>
                      </ScheduleDdayBox>
                      </SmallBoxContainer>
                      <ScheduleDescription>{schedule.description}</ScheduleDescription>
                      <ScheduleDeadline>{schedule.deadline}</ScheduleDeadline>
                  </ScheduleItem>
              ))}
          </div>
          {/* 하단 dot 표시 */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            {schedules.map((_, idx) => (
              <div
                key={idx}
                onClick={() => instanceRef.current?.moveToIdx(idx)}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: currentSlide === idx ? "#000" : "#ccc",
                  margin: "0 4px",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </>)
        :
        <div style={{padding:'10px'}}>
          <EmptyScheduleItem>
            <Icon_EmptyCalendar style={{scale:0.8}}/>
            <span>최근 60일 이내 서류 마감 예정인 모집공고가 없습니다.</span>
          </EmptyScheduleItem>
        </div>}
    </>
    );
}

export default CarouselSchedule